import config from '../config'

type ApiOk<T> = { data: T; error: null }
type ApiErr = { data: null; error: string }
export type ApiResult<T> = ApiOk<T> | ApiErr

type RawResponse<T> = { success: true; data: T } | { success: false; error: string }

let _renewHandler: (() => Promise<string | null>) | null = null

export function registerRenewHandler(fn: () => Promise<string | null>): void {
  _renewHandler = fn
}

function isAuthErrorMessage(message: string | null | undefined): boolean {
  if (!message) return false

  const normalizedMessage = message.trim().toLowerCase()
  return (
    normalizedMessage === 'unauthorized' ||
    normalizedMessage === 'invalid or expired token' ||
    normalizedMessage === 'invalid token type' ||
    normalizedMessage === 'no refresh token' ||
    normalizedMessage === 'invalid or expired refresh token'
  )
}

function shouldAttemptAuthRecovery(path: string, status: number, error: string | null): boolean {
  if (!_renewHandler || path.includes('/api/auth/')) return false
  return status === 401 || isAuthErrorMessage(error)
}

export async function parseResponse<T>(res: Response): Promise<ApiResult<T>> {
  try {
    const json: RawResponse<T> = await res.json()
    if (json.success) return { data: json.data, error: null }
    return { data: null, error: json.error }
  } catch {
    return { data: null, error: `Server error (${res.status})` }
  }
}

export function authHeader(token: string): { Authorization: string } {
  return { Authorization: `Bearer ${token}` }
}

export function resolveApiUrl(path: string): string {
  if (!config.apiBaseUrl) return path
  return new URL(path, config.apiBaseUrl).toString()
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<ApiResult<T>> {
  const baseHeaders = { 'Content-Type': 'application/json', ...(options?.headers as Record<string, string>) }
  const baseOptions: RequestInit = { credentials: 'include', ...options, headers: baseHeaders }
  const url = resolveApiUrl(path)

  try {
    const res = await fetch(url, baseOptions)
    const result = await parseResponse<T>(res)
    if (shouldAttemptAuthRecovery(path, res.status, result.error)) {
      const renewHandler = _renewHandler
      const newToken = renewHandler ? await renewHandler() : null
      if (newToken) {
        const retryRes = await fetch(url, {
          ...baseOptions,
          headers: { ...baseHeaders, Authorization: `Bearer ${newToken}` },
        })
        return parseResponse<T>(retryRes)
      }
    }

    return result
  } catch (e) {
    const message = e instanceof TypeError ? 'Unable to connect to server' : 'An unexpected error occurred'
    return { data: null, error: message }
  }
}

export async function apiFetchBlob(
  path: string,
  options?: RequestInit,
): Promise<ApiResult<{ blob: Blob; headers: Headers }>> {
  const baseHeaders = { ...(options?.headers as Record<string, string>) }
  const baseOptions: RequestInit = { credentials: 'include', ...options, headers: baseHeaders }
  const url = resolveApiUrl(path)

  async function parseBlobResponse(res: Response): Promise<ApiResult<{ blob: Blob; headers: Headers }>> {
    const contentType = res.headers.get('Content-Type')?.toLowerCase() ?? ''
    if (contentType.includes('application/json')) {
      try {
        const json: RawResponse<never> = await res.json()
        if (!json.success) return { data: null, error: json.error }
        return { data: null, error: 'Unexpected JSON response' }
      } catch {
        // Fall through to the generic response handling below.
      }
    }

    if (!res.ok) {
      try {
        const json: RawResponse<never> = await res.json()
        if (!json.success) return { data: null, error: json.error }
      } catch {
        // Fall through to generic error below.
      }
      return { data: null, error: `Server error (${res.status})` }
    }
    return {
      data: {
        blob: await res.blob(),
        headers: res.headers,
      },
      error: null,
    }
  }

  try {
    const res = await fetch(url, baseOptions)
    const result = await parseBlobResponse(res)
    if (shouldAttemptAuthRecovery(path, res.status, result.error)) {
      const renewHandler = _renewHandler
      const newToken = renewHandler ? await renewHandler() : null
      if (newToken) {
        const retryRes = await fetch(url, {
          ...baseOptions,
          headers: { ...baseHeaders, Authorization: `Bearer ${newToken}` },
        })
        return parseBlobResponse(retryRes)
      }
    }

    return result
  } catch (e) {
    const message = e instanceof TypeError ? 'Unable to connect to server' : 'An unexpected error occurred'
    return { data: null, error: message }
  }
}
